import React, { useEffect, useState } from 'react';
import './App.css';
import { Box } from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'material-react-toastify';
import { settingsAtom } from './atoms/Settings';
import { getSettings } from './api/Settings';
import { Waiting } from './components/Waiting/Waiting';
import { AppHeaderDesktop } from './components/AppHeader/AppHeaderDesktop';
import { useIsMobile } from './hooks/useIsMobile';
import { AppHeaderMobile } from './components/AppHeader/AppHeaderMobile';
import { Homepage } from './pages/Homepage/Homepage';
import { FooterDesktop } from './components/Footer/FooterDesktop';
import { FooterMobile } from './components/Footer/FooterMobile';
import { BrandProductsPage } from './pages/Brand/BrandProducts';
import { BrandsPage } from './pages/Brand/BrandsPage';
import { BlogsPage } from './pages/Blog/BlogsPage';
import { BlogPage } from './pages/Blog/BlogPage';
import { PagePage } from './pages/Page/PagePage';
import { CategoryProducts } from './pages/Category/CategoryProducts';
import { ProductDetailsPage } from './pages/Product/ProductDetailsPage';
import { LoginPage } from './pages/Auth/LoginPage';
import { AccountPage } from './pages/Account/Tabs/Panel/AccountPage';
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage';
import { WishlistPage } from './pages/Wishlist/WishlistPage';
import { BasketPage } from './pages/Basket/BasketPage';
import { EditProfilePage } from './pages/Account/Tabs/Profile/EditProfilePage';
import { OrdersPage } from './pages/Account/Tabs/Orders/OrdersPage';
import { OrderDetailsPage } from './pages/Account/Tabs/Orders/OrderDetailsPage';
import { RefundsPage } from './pages/Account/Tabs/Refunds/RefundsPage';
import { AddressesPage } from './pages/Account/Tabs/Address/AddressesPage';
import { ModifyAddressPage } from './pages/Account/Tabs/Address/ModifyAddressPage';
import { RenewPasswordPage } from './pages/Account/Tabs/Password/RenewPasswordPage';
import { SearchResultPage } from './pages/Search/SearchResultPage';
import { CheckoutPage } from './pages/Checkout/CheckoutPage';
import { OrderSuccessPage } from './pages/OrderResult/OrderSuccessPage';
import { OrderErrorPage } from './pages/OrderResult/OrderErrorPage';
import { useFavicon } from './hooks/useFavicon';
import { AnalyticsHelmet } from './components/Helmet/AnalyticsHelmet';
import { SlidingBanner } from './interfaces/SlidingBanner/SlidingBanner';
import { getSlidingBanners } from './api/SlidingBanner';
import { SlidingBannerCmp } from './components/SlidingBanner/SlidingBannerCmp';
import { RouteChangeTracker } from './analytics/RouteChangeTracker';
import { OfferProductsPage } from './pages/Offer/OfferProducts';
import { CookieConsentBanner } from './components/CookieConsentBanner/CookieConsentBanner';
import { useAffiliateTracking } from './hooks/useAffiliateTracking';
import { AffiliateProgramPage } from './pages/Account/Tabs/AffiliateProgram/AffiliateProgramPage';

const App = () => {
  const setSettings = useSetRecoilState(settingsAtom);

  const [slidingBanners, setSlidingBanners] = useState<SlidingBanner[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const isMobile = useIsMobile();

  useAffiliateTracking();
  useFavicon();

  useEffect(() => {
    setLoadingSettings(true);
    getSettings()
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingSettings(false));

    getSlidingBanners()
      .then(setSlidingBanners)
      .catch((err) => toast.error(err.message));
  }, []);

  return loadingSettings ? (
    <Waiting open />
  ) : (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AnalyticsHelmet />
      <RouteChangeTracker />

      {slidingBanners.map((slidingBanner) => (
        <SlidingBannerCmp
          key={slidingBanner.id}
          slidingBanner={slidingBanner}
        />
      ))}

      {isMobile ? <AppHeaderMobile /> : <AppHeaderDesktop />}
      <Routes>
        <Route path='' element={<Homepage />} />
        <Route path='/account' element={<AccountPage />} />
        <Route path='/hesabim' element={<AccountPage />} />
        <Route
          path='/hesabim/partner-programi'
          element={<AffiliateProgramPage />}
        />
        <Route path='/hesabim/profil-duzenle' element={<EditProfilePage />} />
        <Route path='/hesabim/siparislerim' element={<OrdersPage />} />
        <Route
          path='/hesabim/siparis-detayi/:orderID'
          element={<OrderDetailsPage />}
        />
        <Route path='/hesabim/iadelerim' element={<RefundsPage />} />
        <Route path='/hesabim/adres' element={<AddressesPage />} />
        <Route
          path='/hesabim/adres-duzenle/:addressID'
          element={<ModifyAddressPage />}
        />
        <Route path='/hesabim/adres-ekle' element={<ModifyAddressPage />} />
        <Route path='/hesabim/sifre' element={<RenewPasswordPage />} />
        <Route path='/urun/ara/:searchValue' element={<SearchResultPage />} />
        <Route path='/login' element={<LoginPage />} />
        <Route path='/giris-yap' element={<LoginPage />} />
        <Route path='/sifremi-unuttum' element={<ForgotPasswordPage />} />
        <Route
          path='/login/password/reset-password/:email/:code'
          element={<ResetPasswordPage />}
        />
        <Route path='/favoriler' element={<WishlistPage />} />
        <Route path='/sepet' element={<BasketPage />} />
        <Route path='/checkout' element={<CheckoutPage />} />
        <Route path='/payment/info' element={<OrderSuccessPage />} />
        <Route path='/payment/error' element={<OrderErrorPage />} />
        <Route path='/marka/:slug' element={<BrandProductsPage />} />
        <Route path='/markalar' element={<BrandsPage />} />
        <Route path='/kategori/:slug' element={<CategoryProducts />} />
        <Route path='/kampanya/:offerID' element={<OfferProductsPage />} />
        <Route path='/blog' element={<BlogsPage />} />
        <Route path='/blog/:slug' element={<BlogPage />} />
        <Route path='/sayfa/:slug' element={<PagePage />} />
        <Route path='/urun/:slug' element={<ProductDetailsPage />} />
      </Routes>
      {isMobile ? <FooterMobile /> : <FooterDesktop />}
      <CookieConsentBanner />
    </Box>
  );
};

export default App;
